/**
 * Axis Overlay Component for WebGLChart
 * Renders X/Y axis labels and grid lines as HTML overlay
 */

import React, { useMemo } from 'react';
import { ZoomState } from '../types';
import { formatAxisTime, formatNumber } from '../utils/formatters';
import { CHART_PADDING } from '../constants';
import { axisYExtent } from '../utils/zoomFit';

interface AxisOverlayProps {
  width: number;
  height: number;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  zoomState: ZoomState;
  seriesName: string;
}

// Use shared padding constant
const PADDING = CHART_PADDING;

export function computeYTicks(
  yMin: number,
  yMax: number,
  scaleY: number,
  offsetY: number,
  count = 5,
): { value: number; position: number }[] {
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMin >= yMax) return [];
  const { yBottom, yTop } = axisYExtent(yMin, yMax, scaleY, offsetY);
  const step = (yTop - yBottom) / count;
  return Array.from({ length: count + 1 }, (_, i) => ({
    value: yBottom + i * step,
    position: 100 - (i / count) * 100, // inverted for CSS top
  }));
}

export const AxisOverlay: React.FC<AxisOverlayProps> = ({
  width,
  height,
  xMin,
  xMax,
  yMin,
  yMax,
  zoomState,
  seriesName,
}) => {
  // Calculate visible X range based on zoom
  const visibleRange = useMemo(() => {
    // Guard against invalid data
    if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMin >= xMax) {
      return { start: 0, end: 1 };
    }

    const fullRange = xMax - xMin;
    const visibleWidth = fullRange / zoomState.scaleX;

    // Calculate center based on offset
    const centerOffset = zoomState.offsetX / 2;
    const visibleCenter = xMin + fullRange * 0.5 - centerOffset * fullRange / zoomState.scaleX;

    const start = visibleCenter - visibleWidth / 2;
    const end = visibleCenter + visibleWidth / 2;

    // Ensure valid range
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return { start: xMin, end: xMax };
    }

    return { start, end };
  }, [xMin, xMax, zoomState.scaleX, zoomState.offsetX]);

  // Generate X axis ticks (5-7 ticks based on width)
  const xTicks = useMemo(() => {
    const count = Math.min(7, Math.max(3, Math.floor((width - PADDING.left - PADDING.right) / 100)));
    const range = visibleRange.end - visibleRange.start;

    // Guard against zero or invalid range
    if (!Number.isFinite(range) || range <= 0) {
      return [];
    }

    const step = range / count;

    return Array.from({ length: count + 1 }, (_, i) => ({
      value: visibleRange.start + i * step,
      position: (i / count) * 100,
    }));
  }, [visibleRange, width]);

  // Generate Y axis ticks (5 ticks), zoom-aware via axisYExtent
  const yTicks = useMemo(
    () => computeYTicks(yMin, yMax, zoomState.scaleY, zoomState.offsetY),
    [yMin, yMax, zoomState.scaleY, zoomState.offsetY],
  );

  // Chart area dimensions
  const chartWidth = width - PADDING.left - PADDING.right;
  const chartHeight = height - PADDING.top - PADDING.bottom;

  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Y Axis Label (rotated) */}
      <div
        className="absolute text-[10px] sm:text-xs text-muted-foreground font-medium whitespace-nowrap"
        style={{
          left: 0,
          top: '50%',
          transform: 'rotate(-90deg)',
          transformOrigin: 'left center',
          maxWidth: height - PADDING.top - PADDING.bottom,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {seriesName}
      </div>

      {/* Y Axis Ticks and Grid Lines */}
      <div
        className="absolute"
        style={{
          left: PADDING.left - 5,
          top: PADDING.top,
          width: chartWidth + 10,
          height: chartHeight,
        }}
      >
        {yTicks.map((tick, i) => (
          <div
            key={`y-${i}`}
            className="absolute flex items-center"
            style={{
              top: `${tick.position}%`,
              left: -PADDING.left + 5,
              right: 0,
              transform: 'translateY(-50%)',
            }}
          >
            {/* Y value label */}
            <span
              className="text-[9px] sm:text-[10px] text-muted-foreground text-right pr-1 sm:pr-2"
              style={{ width: PADDING.left - 10, minWidth: PADDING.left - 10 }}
            >
              {formatNumber(tick.value)}
            </span>
            {/* Grid line */}
            {i > 0 && i < yTicks.length - 1 && (
              <div
                className="flex-1 border-t border-border"
                style={{ marginLeft: 5 }}
              />
            )}
          </div>
        ))}
      </div>

      {/* X Axis Ticks */}
      <div
        className="absolute flex justify-between"
        style={{
          left: PADDING.left,
          right: PADDING.right,
          bottom: 5,
          height: PADDING.bottom - 10,
        }}
      >
        {xTicks.map((tick, i) => (
          <div
            key={`x-${i}`}
            className="flex flex-col items-center"
            style={{
              position: 'absolute',
              left: `${tick.position}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {/* Tick mark */}
            <div className="w-px h-1.5 bg-border" />
            {/* Time label */}
            <span
              className="text-[8px] sm:text-[9px] text-muted-foreground whitespace-nowrap mt-0.5"
              style={{
                transform: 'rotate(-20deg)',
                transformOrigin: 'top center',
              }}
            >
              {formatAxisTime(tick.value)}
            </span>
          </div>
        ))}
      </div>

      {/* Axis lines */}
      <svg
        className="absolute pointer-events-none"
        style={{
          left: PADDING.left,
          top: PADDING.top,
          width: chartWidth,
          height: chartHeight,
        }}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        preserveAspectRatio="none"
      >
        {/* Left axis line */}
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={chartHeight}
          stroke="#94a3b8"
          strokeWidth="1"
        />
        {/* Bottom axis line */}
        <line
          x1="0"
          y1={chartHeight}
          x2={chartWidth}
          y2={chartHeight}
          stroke="#94a3b8"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
};
